import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';

import type {
  AuthChallengeResponse,
  AuthCompletedResponse,
  SetupMfaMethodPayload,
  Tenant,
  SharedAuthCompletedResponse,
} from '@/_helpers/interfaces';
import broadcastService from '@/_helpers/services/broadcast.service';
import cookieService from '@/_helpers/services/cookie.service';
import tokenService from '@/_helpers/services/token.service';
import { configureApi, getAuthAxios } from '@/_helpers/plugins/api-configuration';
import i18n from '@/_helpers/plugins/i18n';
import router from '@/router';
import { useUserStore } from './user.store';

export const useAuthStore = defineStore('auth', () => {
  // @ts-ignore
  const { t } = i18n.global;

  // state
  const email = ref('');
  const password = ref('');
  const sessionId = ref('');
  const subId = ref('');
  const nextChallenge = ref('');
  const qrCodeUrl = ref('');
  const errorMessage = ref('');

  // Tenant-related state
  const availableTenants = ref<Tenant[]>([]);
  const selectedTenant = ref<Tenant | null>(null);
  const needsTenantSelection = ref(false);

  // actions
  const extractSessionData = (data: AuthChallengeResponse): void => {
    const { session, sub, challenge, extra } = data;

    sessionId.value = session;
    subId.value = sub;
    nextChallenge.value = challenge;
    qrCodeUrl.value = extra.qr_code;
  };

  const completeLoginFlow = (data: AuthCompletedResponse): void => {
    const { access_token, refresh_token } = data;

    tokenService.setAccessToken(access_token);
    tokenService.setRefreshToken(refresh_token);

    router.push('/home');
  };

  const handleSharedAuthSuccess = (data: SharedAuthCompletedResponse): void => {
    const { access_token, refresh_token, tenants, last_accessed_tenant } = data;

    // Store tokens
    tokenService.setAccessToken(access_token);
    tokenService.setRefreshToken(refresh_token);

    // Store tenants
    availableTenants.value = tenants;

    // Determine which tenant to use
    if (last_accessed_tenant) {
      // Use last accessed tenant
      selectTenantInternal(last_accessed_tenant);
    } else if (tenants.length === 1) {
      // Auto-select if only one tenant
      selectTenantInternal(tenants[0]);
    } else if (tenants.length > 1) {
      // Need user to select a tenant
      needsTenantSelection.value = true;
      return; // Don't navigate yet
    }

    router.push('/home');
  };

  const selectTenantInternal = (tenant: Tenant): void => {
    selectedTenant.value = tenant;
    cookieService.setAuthToken('tenant', tenant.name);
    configureApi();
  };

  const enterCredentials = async (
    userEmail: string,
    userPassword: string
  ): Promise<AuthChallengeResponse | SharedAuthCompletedResponse> => {
    try {
      email.value = userEmail;
      password.value = userPassword;

      // Use the tenant-agnostic auth endpoint
      const authAxios = getAuthAxios();
      const { data } = await authAxios.post(`/auth/login/`, {
        email: userEmail,
        password: userPassword,
      });

      errorMessage.value = '';
      if (data.challenge) {
        extractSessionData(data);
      } else {
        // New shared auth response with tenant info
        handleSharedAuthSuccess(data);
      }

      return data;
    } catch (error: any) {
      const errorType = error.response?.data?.type;

      if (errorType === 'validation_error') {
        // Check if it's a "no tenants" error
        const errorMsg = error.response?.data?.errors?.[0]?.detail || '';
        if (errorMsg.includes('workspace') || errorMsg.includes('tenant')) {
          errorMessage.value = t('auth.errors.no_workspace');
        } else {
          errorMessage.value = t('auth.errors.invalid_credentials');
        }
      } else {
        errorMessage.value = t('common.errors.something_went_wrong');
      }

      throw error;
    }
  };

  const createNewPassword = async (
    newPassword: string
  ): Promise<AuthChallengeResponse | SharedAuthCompletedResponse> => {
    try {
      password.value = newPassword;

      const authAxios = getAuthAxios();
      const { data } = await authAxios.post(`/auth/respond_to_new_password_required_challenge/`, {
        new_password: newPassword,
        session: sessionId.value,
        sub: subId.value,
      });

      errorMessage.value = '';
      if (data.challenge) {
        extractSessionData(data);
      } else {
        handleSharedAuthSuccess(data);
      }

      return data;
    } catch (error: any) {
      errorMessage.value = t('common.errors.something_went_wrong');

      throw error;
    }
  };

  const getSetupMfaMethodRequestPayload = (
    mfaMethod: string,
    options: { code?: string; mobileNumber?: string }
  ): SetupMfaMethodPayload => {
    const requestPayload: SetupMfaMethodPayload = {
      session: sessionId.value,
      sub: subId.value,
      mfa_method: mfaMethod,
    };

    if (mfaMethod === 'TOTP') {
      requestPayload.mfa_code = options.code;
    } else {
      requestPayload.phone_number = options.mobileNumber;
      requestPayload.email = email.value;
      requestPayload.password = password.value;
    }

    return requestPayload;
  };

  const setupMfaMethod = async (
    mfaMethod: 'TOTP' | 'SMS',
    options: {
      code?: string;
      mobileNumber?: string;
    }
  ): Promise<AuthChallengeResponse | SharedAuthCompletedResponse> => {
    try {
      const requestPayload: any = getSetupMfaMethodRequestPayload(mfaMethod, options);

      const authAxios = getAuthAxios();
      const { data } = await authAxios.post(
        `/auth/respond_to_mfa_setup_challenge/`,
        requestPayload
      );

      errorMessage.value = '';

      if (data.challenge) {
        extractSessionData(data);
      } else if (data.access_token) {
        handleSharedAuthSuccess(data);
      }

      return data;
    } catch (error: any) {
      const errorType = error.response?.data?.type;

      if (errorType === 'validation_error') {
        errorMessage.value = t('auth.errors.invalid_mfa_code');
      } else {
        errorMessage.value = t('common.errors.something_went_wrong');
      }

      throw error;
    }
  };

  const verifyCode = async (
    code: string,
    verificationMethod: 'SMS' | 'TOTP'
  ): Promise<SharedAuthCompletedResponse> => {
    try {
      const authAxios = getAuthAxios();
      const { data } = await authAxios.post(`/auth/respond_to_mfa_challenge/`, {
        session: sessionId.value,
        sub: subId.value,
        mfa_code: code,
        mfa_method: verificationMethod === 'TOTP' ? 'SOFTWARE_TOKEN_MFA' : 'SMS_MFA',
      });

      errorMessage.value = '';
      handleSharedAuthSuccess(data);

      return data;
    } catch (error: any) {
      const errorType = error.response?.data?.type;

      if (errorType === 'validation_error') {
        errorMessage.value = t('auth.errors.invalid_mfa_code');
      } else {
        errorMessage.value = t('common.errors.something_went_wrong');
      }

      throw error;
    }
  };

  const logout = () => {
    const { currentUser } = storeToRefs(useUserStore());

    email.value = '';
    password.value = '';
    sessionId.value = '';
    subId.value = '';
    nextChallenge.value = '';
    qrCodeUrl.value = '';
    errorMessage.value = '';
    availableTenants.value = [];
    selectedTenant.value = null;
    needsTenantSelection.value = false;

    currentUser.value = null;

    // Clear all authentication cookies
    cookieService.clear();

    // Notify other tabs about logout
    broadcastService.notifyLogout();

    router.push('/login');
  };

  const selectTenant = async (tenantId: number): Promise<void> => {
    try {
      const authAxios = getAuthAxios();
      const { data } = await authAxios.post(`/auth/select_tenant/`, {
        tenant_id: tenantId,
      });

      selectTenantInternal(data);
      needsTenantSelection.value = false;

      router.push('/home');
    } catch (error: any) {
      errorMessage.value = t('common.errors.something_went_wrong');
      throw error;
    }
  };

  const switchTenant = async (tenantId: number): Promise<void> => {
    try {
      const authAxios = getAuthAxios();
      const { data } = await authAxios.post(`/auth/select_tenant/`, {
        tenant_id: tenantId,
      });

      // Set the tenant cookie for the new tenant
      cookieService.setAuthToken('tenant', data.name);

      // Clear selected customer so the new tenant will auto-select first customer
      cookieService.remove('selectedCustomerId');

      // Set a flag to indicate we're in the middle of a tenant switch
      // This prevents NavBar from triggering a second reload
      cookieService.setSessionCookie('tenantSwitchInProgress', 'true');

      // Reload the current page to refresh data for new tenant
      window.location.reload();
    } catch (error: any) {
      errorMessage.value = t('common.errors.something_went_wrong');
      throw error;
    }
  };

  const fetchUserTenants = async (): Promise<void> => {
    try {
      // Get tenants from /auth/tenants endpoint (tenant-agnostic)
      const authAxios = getAuthAxios();
      const { data } = await authAxios.get(`/auth/tenants/`);

      if (data.tenants) {
        availableTenants.value = data.tenants;
      }
    } catch (error: any) {
      console.error('Failed to fetch user tenants:', error);
    }
  };

  const sendPasswordResetMail = async (email: string) => {
    try {
      const { data } = await getAuthAxios().post(`/auth/forgot_password/`, {
        email,
      });

      errorMessage.value = '';

      return data;
    } catch (error: any) {
      errorMessage.value = t('common.errors.something_went_wrong');

      throw error;
    }
  };

  return {
    // State
    sessionId,
    subId,
    nextChallenge,
    qrCodeUrl,
    errorMessage,
    availableTenants,
    selectedTenant,
    needsTenantSelection,
    // Auth actions
    enterCredentials,
    completeLoginFlow,
    createNewPassword,
    setupMfaMethod,
    verifyCode,
    logout,
    sendPasswordResetMail,
    // Tenant actions
    selectTenant,
    switchTenant,
    fetchUserTenants,
  };
});
